import React from 'react';

import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { ArticleSummary, getArticle } from 'stores/ArticleStoreAdapter';
import { formatDate } from 'utils/DateTimeFormatter';

import { useKnzDocument } from '../../KnzDocumentContext';
import { KnzComponentProps } from '../../types';
import { formatAuthors } from './articleNavUtils';

/**
 * 記事詳細ルート（任意で section アンカー付き）。
 * section を指定すると `#<anchor>` を付け、遷移先記事の該当 Section へスクロールさせる
 * （Section は anchor を要素 id として描画するため、記事詳細側でハッシュを見てスクロールする）。
 */
const articlePath = (articleId: string, section?: string): string =>
  section ? `/devdocs/article/${articleId}#${section}` : `/devdocs/article/${articleId}`;

/** 設定エラー・取得失敗を枠内に表示する（ArticleNav と同じ扱い）。 */
const CardError: React.FC<{ message: string }> = ({ message }) => (
  <Box sx={{ border: 1, borderColor: 'error.main', borderRadius: 2, p: 1.5, maxWidth: 360 }}>
    <Stack direction="row" spacing={1} alignItems="center">
      <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 18 }} />
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </Stack>
  </Box>
);

/**
 * 記事リンクカード。
 *
 * articleId で指定した記事を getArticle（API6202）で動的に取得し、タイトル・説明・著者・公開日を
 * 埋め込んで表示する。クリックで対象記事を新規タブで開く（LinkCard と同様に別タブ。元の記事を
 * 開いたまま参照先を見られるようにするため、記事詳細・プレビューとも新規タブで開く）。
 * 任意の section（Section の anchor）を指定すると、遷移先記事の該当セクションへスクロールする。
 *
 * 説明欄は、text（任意の説明）が指定されていればそれを優先し、無ければ記事の description を出す。
 * section（特定セクションへ飛ばす）を使うときは、記事全体の description ではなくそのセクションを
 * 説明する文を text に自分で書くとよい。
 *
 * - 動的取得・エラー挙動は ArticleNav に合わせる:
 *   - 取得中: ローディング表示
 *   - 設定ミス（articleId 未指定）: 枠内にエラー表示（致命的にしない）
 *   - 取得失敗（存在しない articleId / 参照不可）: 枠内にエラー表示
 * - 遷移は常に新規タブで開く（記事詳細・プレビューとも）。元の記事を開いたまま参照先を別タブで
 *   見られるようにするため。
 * - UI 文言は KnzDocument の navContext で出し分ける（本文フロー中のブロックのため ArticleNav の
 *   上下スロット Context ではなくドキュメント全体の navContext を見る）:
 *   - 'article'（記事詳細）: 「記事を開く」
 *   - 'preview'（ワークスペースのプレビュー・既定）: 「別タブで開く」
 */
export const ArticleLinkCard: React.FC<KnzComponentProps> = ({ node }) => {
  const { navContext } = useKnzDocument();

  const articleId = ((node.params.articleId as string) ?? '').trim();
  const section = ((node.params.section as string) ?? '').trim() || undefined;
  const color = (node.params.color as string) || 'primary.main';
  // 任意の説明。指定されていれば記事の description より優先する。
  const customText = ((node.params.text as string) ?? '').trim() || undefined;

  const [state, setState] = React.useState<{
    loading: boolean;
    article?: ArticleSummary;
    failed: boolean;
  }>({ loading: true, failed: false });

  React.useEffect(() => {
    if (!articleId) {
      setState({ loading: false, failed: false });
      return;
    }
    let active = true;
    setState({ loading: true, failed: false });
    getArticle(articleId)
      .then((article) => {
        if (active) setState({ loading: false, article, failed: false });
      })
      .catch(() => {
        if (active) setState({ loading: false, failed: true });
      });
    return () => {
      active = false;
    };
  }, [articleId]);

  // 常に新規タブで開く（記事詳細・プレビューとも）。元の記事を開いたまま参照先を別タブで見られるようにする。
  const handleOpen = React.useCallback(() => {
    const path = articlePath(articleId, section);
    window.open(path, '_blank', 'noopener,noreferrer');
  }, [articleId, section]);

  // 設定ミス（articleId 未指定）
  if (!articleId) {
    return <CardError message="ArticleLinkCard: articleId が指定されていません" />;
  }

  // 取得中
  if (state.loading) {
    return (
      <Box
        sx={{
          display: 'block',
          width: 360,
          maxWidth: '100%',
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ height: 4, bgcolor: color }} />
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ p: 2, color: 'text.secondary' }}
        >
          <CircularProgress size={16} />
          <Typography variant="caption">記事を読み込み中...</Typography>
        </Stack>
      </Box>
    );
  }

  // 取得失敗（存在しない / 参照不可）
  if (state.failed || !state.article) {
    return <CardError message="ArticleLinkCard: 記事が見つかりません" />;
  }

  const article = state.article;
  const authorText = formatAuthors(article.authors);
  // text（任意の説明）を優先し、無ければ記事の description をフォールバック表示する。
  const descriptionText = customText ?? article.description;

  return (
    <ButtonBase
      onClick={handleOpen}
      sx={{
        display: 'block',
        width: 360,
        maxWidth: '100%',
        textAlign: 'left',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'border-color 0.2s',
        '&:hover': { borderColor: color },
      }}
    >
      <Box sx={{ height: 4, bgcolor: color }} />
      <Stack spacing={1} sx={{ p: 2 }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <ArticleOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />
          <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.4 }}>
            {article.title}
          </Typography>
        </Stack>
        {descriptionText && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              lineHeight: 1.5,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {descriptionText}
          </Typography>
        )}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.disabled' }}>
          {authorText && (
            <Stack direction="row" spacing={0.25} alignItems="center" sx={{ minWidth: 0 }}>
              <PersonOutlineIcon sx={{ fontSize: 13 }} />
              <Typography
                variant="caption"
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {authorText}
              </Typography>
            </Stack>
          )}
          <Typography variant="caption">{formatDate(article.createdAt)}</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ alignSelf: 'flex-end' }}>
          <Typography variant="caption" color="text.disabled">
            {navContext === 'article' ? '記事を開く' : '別タブで開く'}
          </Typography>
          <OpenInNewIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
        </Stack>
      </Stack>
    </ButtonBase>
  );
};

export default ArticleLinkCard;
